import { jsPDF } from 'jspdf';
import { NOTE_TYPE_LABELS } from '../constants/schemas';
import {
  formatCareTimeForPdf,
  formatCareTimeLabel,
  normalizeCareTime,
  resolveNoteTiming,
} from '../utils/lateEntry';
import { formatAddendumAddedAt, getNoteAddendums } from '../utils/addendum';

function addWrappedText(doc, text, x, y, maxWidth, lineHeight = 6) {
  const lines = doc.splitTextToSize(text || '', maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function ensureSpace(doc, y, needed = 30) {
  if (y <= 260 - needed) return y;
  doc.addPage();
  return 20;
}

function addSection(doc, title, body, margin, y, maxWidth) {
  y = ensureSpace(doc, y);
  doc.setFont(undefined, 'bold');
  y = addWrappedText(doc, title, margin, y, maxWidth);
  doc.setFont(undefined, 'normal');
  y = addWrappedText(doc, body || '—', margin, y, maxWidth) + 4;
  return y;
}

export function generatePDF({
  note,
  noteType,
  patientName,
  room,
  datetime,
  careTime: careTimeProp = undefined,
  isLateEntry: isLateEntryProp = undefined,
  editedContent,
  addendums: addendumsProp,
}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;
  let y = 20;

  const content = editedContent || note;
  const timing = resolveNoteTiming({
    datetime,
    documentation_time: content?.documentation_time,
    care_time: careTimeProp,
    care_day_offset: content?.care_day_offset,
    is_late_entry: isLateEntryProp,
    openaiNote: content,
    note_content: content,
  });
  const careTime =
    careTimeProp !== undefined ? normalizeCareTime(careTimeProp) || careTimeProp : timing.careTime;
  const isLateEntry =
    typeof isLateEntryProp === 'boolean' ? isLateEntryProp : timing.isLateEntry;

  doc.setFontSize(22);
  doc.setTextColor(30, 64, 175);
  doc.text('Sawtna', margin, y);
  y += 12;

  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  doc.text(`Patient: ${patientName}`, margin, y);
  doc.text(`Room: ${room}`, pageWidth - margin - 40, y);
  y += 8;

  const noteLabel = NOTE_TYPE_LABELS[noteType] || noteType;

  doc.text(`Note Type: ${noteLabel}`, margin, y);
  y += 6;
  const careTimeLabel = formatCareTimeForPdf(
    careTime,
    timing.documentationTime || datetime,
    timing.careDayOffset
  );
  doc.text(`Time of care: ${careTimeLabel}`, margin, y);
  if (isLateEntry) {
    const careLine = `Time of care: ${careTimeLabel}`;
    const careWidth = doc.getTextWidth(careLine);
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128); // #6B7280
    doc.text('Late Entry', margin + careWidth + 4, y);
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
  }
  y += 12;

  doc.setDrawColor(30, 64, 175);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;

  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);

  if (content?.corti_export) {
    doc.setFont(undefined, 'bold');
    y = addWrappedText(doc, 'Clinical Note', margin, y, maxWidth);
    doc.setFont(undefined, 'normal');
    y = addWrappedText(doc, content.corti_export, margin, y, maxWidth) + 8;
  } else if (noteType === 'progress_note' && (content?.narrative || content?.soap)) {
    const narrative = typeof content.narrative === 'string' ? content.narrative.trim() : '';
    if (narrative) {
      doc.setFont(undefined, 'bold');
      y = addWrappedText(doc, 'Progress Note', margin, y, maxWidth);
      doc.setFont(undefined, 'normal');
      y = addWrappedText(doc, narrative, margin, y, maxWidth) + 4;
    } else {
      const sections = [
        ['S — Subjective', content.soap?.subjective],
        ['O — Objective', content.soap?.objective],
        ['A — Assessment', content.soap?.assessment],
        ['P — Plan', content.soap?.plan],
      ];
      sections.forEach(([title, body]) => {
        doc.setFont(undefined, 'bold');
        y = addWrappedText(doc, title, margin, y, maxWidth);
        doc.setFont(undefined, 'normal');
        y = addWrappedText(doc, body || '', margin, y, maxWidth) + 4;
        if (y > 260) {
          doc.addPage();
          y = 20;
        }
      });
    }

    const vitals = content.vitals_extracted;
    const hasVitals = vitals && Object.values(vitals).some((v) => v !== null && v !== '');
    if (hasVitals) {
      doc.setFont(undefined, 'bold');
      y = addWrappedText(doc, 'Extracted Vitals', margin, y, maxWidth);
      doc.setFont(undefined, 'normal');
      const vitalLines = [
        `BP: ${vitals.bp || '—'}`,
        `Pulse: ${vitals.pulse ?? '—'}`,
        `Temp: ${vitals.temp_celsius ?? '—'}°C`,
        `O2 Sat: ${String(vitals.o2_sat ?? '—').replace(/%+\s*$/g, '')}%`,
        `RR: ${vitals.rr ?? '—'}`,
        `Pain: ${vitals.pain ?? '—'}/10`,
      ].join('  |  ');
      y = addWrappedText(doc, vitalLines, margin, y, maxWidth) + 4;
    }
  } else if (noteType === 'vitals' && content?.vitals) {
    doc.setFont(undefined, 'bold');
    y = addWrappedText(doc, 'Vital Signs', margin, y, maxWidth);
    doc.setFont(undefined, 'normal');
    const v = content.vitals;
    const lines = [
      `BP: ${v.bp_systolic ?? '—'}/${v.bp_diastolic ?? '—'} mmHg`,
      `Pulse: ${v.pulse ?? '—'} bpm`,
      `Temp: ${v.temp_celsius ?? '—'}°C`,
      `O2 Sat: ${String(v.o2_sat ?? '—').replace(/%+\s*$/g, '')}%`,
      `RR: ${v.rr ?? '—'}/min`,
      `Pain: ${v.pain ?? '—'}/10`,
      `Weight: ${v.weight_kg ?? '—'} kg`,
      `Blood Sugar: ${v.blood_sugar ?? '—'} mg/dL`,
    ];
    lines.forEach((line) => {
      y = addWrappedText(doc, line, margin, y, maxWidth);
    });
    y += 4;
  } else if (noteType === 'handover' && content?.patients?.length) {
    content.patients.forEach((patientEntry, patientIndex) => {
      if (patientIndex > 0) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      y = addWrappedText(
        doc,
        `${patientEntry.patient_name || 'Patient'}${patientEntry.room ? ` — Room ${patientEntry.room}` : ''}`,
        margin,
        y,
        maxWidth
      );
      doc.setFont(undefined, 'normal');
      y += 4;

      const narrative =
        (typeof patientEntry.narrative === 'string' && patientEntry.narrative.trim())
        || [
          patientEntry.sbar?.situation,
          patientEntry.sbar?.background,
          patientEntry.sbar?.assessment,
          patientEntry.sbar?.recommendation,
        ]
          .map((value) => String(value || '').trim())
          .filter(Boolean)
          .join('\n\n');
      y = addWrappedText(doc, narrative || '—', margin, y, maxWidth) + 4;

      if (patientEntry.outstanding_tasks?.length) {
        doc.setFont(undefined, 'bold');
        y = addWrappedText(doc, 'Outstanding Tasks', margin, y, maxWidth);
        doc.setFont(undefined, 'normal');
        patientEntry.outstanding_tasks.forEach((task) => {
          const label = typeof task === 'string' ? task : task.text;
          const checked = typeof task === 'object' && task.completed ? '[x]' : '[ ]';
          y = addWrappedText(doc, `${checked} ${label}`, margin, y, maxWidth);
        });
        y += 4;
      }
    });
  } else if (noteType === 'handover' && (content?.narrative || content?.sbar)) {
    const narrative =
      (typeof content.narrative === 'string' && content.narrative.trim())
      || [
        content.sbar?.situation,
        content.sbar?.background,
        content.sbar?.assessment,
        content.sbar?.recommendation,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join('\n\n');
    y = addWrappedText(doc, narrative || '—', margin, y, maxWidth) + 4;

    if (content.outstanding_tasks?.length) {
      doc.setFont(undefined, 'bold');
      y = addWrappedText(doc, 'Outstanding Tasks', margin, y, maxWidth);
      doc.setFont(undefined, 'normal');
      content.outstanding_tasks.forEach((task) => {
        const label = typeof task === 'string' ? task : task.text;
        const checked = typeof task === 'object' && task.completed ? '[x]' : '[ ]';
        y = addWrappedText(doc, `${checked} ${label}`, margin, y, maxWidth);
      });
      y += 4;
    }
  } else if (noteType === 'discharge') {
    const narrative = typeof content.narrative === 'string' ? content.narrative.trim() : '';
    if (narrative) {
      y = addWrappedText(doc, narrative, margin, y, maxWidth) + 4;
    } else {
      y = addSection(doc, 'Condition at Discharge', content.condition_at_discharge, margin, y, maxWidth);
      y = addSection(doc, 'Discharge Mode', content.discharge_mode, margin, y, maxWidth);
      y = addSection(doc, 'Accompanied By', content.accompanied_by, margin, y, maxWidth);

      const education = Array.isArray(content.education)
        ? content.education.filter(Boolean).map((item) => `• ${item}`).join('\n')
        : '';
      y = addSection(doc, 'Discharge Education', education || '—', margin, y, maxWidth);

      const meds = Array.isArray(content.discharge_medications)
        ? content.discharge_medications
        : [];
      if (meds.length) {
        y = ensureSpace(doc, y);
        doc.setFont(undefined, 'bold');
        y = addWrappedText(doc, 'Discharge Medications', margin, y, maxWidth);
        doc.setFont(undefined, 'normal');
        meds.forEach((med) => {
          const label =
            typeof med === 'string'
              ? med
              : [med?.name || med?.drug_name, med?.dose, med?.frequency, med?.route]
                  .filter(Boolean)
                  .join(' — ');
          y = addWrappedText(doc, `• ${label || '—'}`, margin, y, maxWidth);
          y = ensureSpace(doc, y, 12);
        });
        y += 4;
      }

      const followUp = content.follow_up || {};
      const followUpText = [followUp.physician, followUp.timeframe, followUp.location]
        .filter((part) => typeof part === 'string' && part.trim())
        .join(' — ');
      y = addSection(doc, 'Follow-up', followUpText || '—', margin, y, maxWidth);
      y = addSection(doc, 'Special Instructions', content.special_instructions, margin, y, maxWidth);

      const understanding = [
        content.understanding_patient ? 'Patient understands' : null,
        content.understanding_family ? 'Family understands' : null,
      ]
        .filter(Boolean)
        .join('; ');
      if (understanding) {
        y = addSection(doc, 'Understanding Confirmed', understanding, margin, y, maxWidth);
      }
    }
  }

  const negations = content?.negations_detected;
  if (negations?.length) {
    if (y > 240) {
      doc.addPage();
      y = 20;
    }
    doc.setFont(undefined, 'bold');
    y = addWrappedText(doc, 'Negations Noted (verified absent):', margin, y, maxWidth);
    doc.setFont(undefined, 'normal');
    negations.forEach((n) => {
      y = addWrappedText(doc, `• ${n}`, margin, y, maxWidth);
    });
    y += 4;
  }

  const addendums =
    Array.isArray(addendumsProp) && addendumsProp.length
      ? addendumsProp
      : getNoteAddendums(content);
  if (addendums.length) {
    y = ensureSpace(doc, y, 40);
    doc.setDrawColor(180, 180, 180);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;
    addendums.forEach((item) => {
      y = ensureSpace(doc, y, 30);
      doc.setFont(undefined, 'bold');
      y = addWrappedText(
        doc,
        `Addendum — added at ${formatAddendumAddedAt(item.addendum_time)}`,
        margin,
        y,
        maxWidth
      );
      doc.setFont(undefined, 'normal');
      if (item.addendum_care_time) {
        y = addWrappedText(
          doc,
          `Time of care: ${formatCareTimeLabel(item.addendum_care_time, item.care_day_offset || 0)}`,
          margin,
          y,
          maxWidth
        );
      }
      y = addWrappedText(doc, item.addendum_text || '—', margin, y, maxWidth) + 6;
    });
  }

  if (y > 250) {
    doc.addPage();
    y = 20;
  }

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, 270, pageWidth - margin, 270);
  doc.setFontSize(11);
  doc.text('Reviewed and approved by: _______________________', margin, 280);

  const filename = `Sawtna_${patientName.replace(/\s+/g, '_')}_${noteType}_${Date.now()}.pdf`;
  doc.save(filename);
  return filename;
}
